/**
 * AssureX AI Chatbot — floating chat bubble
 * Tier 1: Read-only QA analytics assistant
 */
import React, { useState, useRef, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ChatBot.css';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
  "What is the overall pass rate?",
  "How many test scenarios were created this month?",
  "Which user has written the most test scenarios?",
  "Show me stats for the last 30 days",
  "How many test runs are completed?",
];

export const ChatBot: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgId, setMsgId] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const nextId = () => { setMsgId(p => p + 1); return msgId + 1; };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Show welcome message
      setMessages([{
        id: 0,
        role: 'assistant',
        text: `Hi ${user?.name?.split(' ')[0] || 'there'} 👋 I'm your AssureX AI Assistant. Ask me anything about your QA metrics, test coverage, user activity, or project status.`,
        timestamp: new Date(),
      }]);
    }
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]); // eslint-disable-line

  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg: Message = { id: nextId(), role: 'user', text: msg, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const resp = await apiClient.chatbotMessage(msg);
      const botMsg: Message = { id: nextId(), role: 'assistant', text: resp.answer, timestamp: new Date() };
      setMessages(prev => [...prev, botMsg]);
    } catch (err: any) {
      const errMsg: Message = {
        id: nextId(),
        role: 'assistant',
        text: err?.response?.data?.detail || "Sorry, I couldn't get an answer right now. Please try again.",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const clearChat = () => {
    setMessages([{
      id: 0,
      role: 'assistant',
      text: `Chat cleared. How can I help you?`,
      timestamp: new Date(),
    }]);
  };

  return (
    <>
      {/* Chat window */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-avatar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </div>
              <div>
                <div className="chatbot-title">AssureX AI</div>
                <div className="chatbot-subtitle">QA Analytics Assistant</div>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button onClick={clearChat} className="chatbot-icon-btn" title="Clear chat">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                </svg>
              </button>
              <button onClick={() => setIsOpen(false)} className="chatbot-icon-btn" title="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`chatbot-msg chatbot-msg-${msg.role}`}>
                {msg.role === 'assistant' && (
                  <div className="chatbot-msg-avatar">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                      <path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
                    </svg>
                  </div>
                )}
                <div className="chatbot-msg-content">
                  <div className="chatbot-msg-bubble">
                    {msg.text.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < msg.text.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="chatbot-msg-time">{formatTime(msg.timestamp)}</div>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="chatbot-msg chatbot-msg-assistant">
                <div className="chatbot-msg-avatar">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
                  </svg>
                </div>
                <div className="chatbot-msg-content">
                  <div className="chatbot-typing">
                    <span/><span/><span/>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions — only shown at the start */}
          {messages.length === 1 && (
            <div className="chatbot-suggestions">
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <button key={i} className="chatbot-suggestion" onClick={() => sendMessage(q)}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="chatbot-input-wrap">
            <input
              ref={inputRef}
              className="chatbot-input"
              type="text"
              placeholder="Ask about test metrics, coverage, users..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
              disabled={loading}
              maxLength={500}
            />
            <button
              className="chatbot-send-btn"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              title="Send"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

          <div className="chatbot-footer">
            Powered by Gemini · AssureX AI Assistant
          </div>
        </div>
      )}

      {/* Floating bubble */}
      <button
        className={`chatbot-bubble ${isOpen ? 'chatbot-bubble-open' : ''}`}
        onClick={() => setIsOpen(v => !v)}
        title="AssureX AI Assistant"
      >
        {isOpen ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
        {!isOpen && <span className="chatbot-bubble-badge">AI</span>}
      </button>
    </>
  );
};
